import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import TextFieldLabel from "app/components/common/TextFieldLabel";
import {
  selectActiveSession,
  selectSelectedSchoolId,
} from "app/modules/schools/selectors";
import {
  CustomSelectBox,
  CustomTextField,
} from "assets/styles/globalStyledComponent";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import ErrorText from "../CreateStudent/components/ErrorText";
import { DatePicker } from "@mui/x-date-pickers";
import { bulkPromoteByClass } from "app/services/schoolService/studentBulk.service";
import Grid2 from "@mui/material/Unstable_Grid2";
import { Info } from "@mui/icons-material";
import { getAllSessions } from "app/services/management";
import { getStudentForPromotionOrMigrationLogic } from "app/services/student-management";
import { useClassData } from "app/hooks/globallyApplicable/useClassData";
import CommonTable1 from "app/components/Tables/CommonTable1";
import appRoutes from "app/config/routes";

const BulkPromotion = () => {
  const schoolDocId = useSelector(selectSelectedSchoolId);
  const selectedSession = useSelector(selectActiveSession);
  const isMobile = useMediaQuery("(max-width:600px)");
  const [state, setState] = React.useState({
    sections: [],
    toSections: [],
    open: false,
    confirmDialogOpen: false,
    formData: null,
  });
  const [nextSession, setNextSession] = React.useState("");
  const [nextSessionError, setNextSessionError] = React.useState("");

  const initialState = {
    fromClassDocId: "",
    fromSection: "",
    admissionDate: new Date(),
    toClassDocId: "",
    toSection: "",
  };

  const { control, watch, handleSubmit, reset } = useForm({
    defaultValues: initialState,
    mode: "onBlur",
  });

  const fromClassDocId = watch("fromClassDocId");
  const fromSection = watch("fromSection");

  const queryClient = useQueryClient();
  const [selectedPromotionStudents, setSelectedPromotionStudents] = useState(
    [],
  );

  const {
    data: promotionStudentsResponse,
    isFetching: promotionStudentsFetching,
  } = useQuery({
    queryKey: [
      "getStudentForPromotionOrMigration",
      selectedSession,
      fromClassDocId,
      fromSection,
    ],
    queryFn: () =>
      getStudentForPromotionOrMigrationLogic({
        session: selectedSession,
        classList: JSON.stringify([
          { classDocId: fromClassDocId, section: fromSection },
        ]),
      }),
    enabled: !!selectedSession && !!fromClassDocId && !!fromSection,
    onError: (error) => toast.error(error?.response?.data?.message),
  });

  const promotionStudents = promotionStudentsResponse?.data?.list ?? [];

  useEffect(() => {
    setSelectedPromotionStudents([]);
  }, [fromClassDocId, fromSection]);

  const handleStudentSelectionChange = (updatedData) => {
    setSelectedPromotionStudents(updatedData.filter((item) => item?.checked));
  };

  const promotionStudentsColumns = useMemo(
    () => [
      {
        header: "Sr.No",
        Cell: ({ cell }) => parseInt(cell.row.id) + 1,
        size: 110,
        maxSize: 110,
      },
      {
        header: "Student Name",
        accessorKey: "studentName",
        Cell: ({
          cell: {
            row: { original },
          },
        }) => (
          <Link
            to={`/${appRoutes.student_profile}/${original?.studentDocId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Typography>{original?.studentName ?? "--"}</Typography>
          </Link>
        ),
        maxSize: 120,
      },
      {
        header: "Admission Number",
        accessorKey: "admissionNumber",
        maxSize: 100,
      },
      {
        header: "Gender",
        accessorFn: (row) => row?.gender ?? "--",
        maxSize: 60,
      },
      {
        header: "Father's Name",
        accessorFn: (row) => row?.fatherName ?? "--",
        maxSize: 100,
      },
      {
        header: "Student Type",
        accessorKey: "studentAdmissionType",
        Cell: ({ cell }) => cell?.row?.original?.studentAdmissionType ?? "--",
        maxSize: 80,
      },
    ],
    [],
  );

  const { data: allSessionsResponse } = useQuery({
    queryKey: ["getAllSessions", schoolDocId],
    queryFn: () => getAllSessions(schoolDocId),
    enabled: !!schoolDocId,
    onError: (err) => toast.error(err?.response?.data?.message),
  });
  const allSessions = allSessionsResponse?.data?.list || [];

  const { classList: classData, error: classDataError } = useClassData();
  useEffect(() => {
    if (classDataError) {
      toast.error(classDataError?.response?.data?.message);
    }
  }, [classDataError]);

  const {
    mutate: bulkPromoteByClassMutate,
    isLoading: bulkPromoteByClassLoading,
  } = useMutation(bulkPromoteByClass, {
    onSuccess: ({ data }) => {
      handleConfirmDialogClose();
      handleReset();
      queryClient.invalidateQueries(["getStudentForPromotionOrMigration"]);
      toast.success(data?.message);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    },
  });

  const CustomSelect = ({ control, name, rules, options = [] }) => {
    return (
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <CustomSelectBox
            native
            size="small"
            fullWidth
            displayEmpty
            onChange={onChange}
            value={value}
            error={!!error}
          >
            <MenuItem value={""} component="option">
              <em>Select</em>
            </MenuItem>
            {options?.map((item) => (
              <MenuItem value={item} key={item} component="option">
                {item}
              </MenuItem>
            ))}
          </CustomSelectBox>
        )}
      />
    );
  };
  const CustomDatePicker = ({ control, name, rules }) => {
    return (
      control && (
        <Controller
          name={name}
          control={control}
          rules={rules}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <DatePicker
              inputFormat="DD-MM-YYYY"
              onChange={(e) => onChange(e)}
              value={value}
              renderInput={(params) => (
                <CustomTextField {...params} size="small" fullWidth />
              )}
            />
          )}
        />
      )
    );
  };

  const currentSessionBox = () => {
    return (
      <>
        <Stack direction="row" alignItems="center" gap={2} sx={{ mb: 1 }}>
          <TextFieldLabel title={`Current Session : `} />
          <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
            {selectedSession}
          </Typography>
        </Stack>
        <Grid
          container
          spacing={1}
          sx={{
            padding: "2",
            marginTop: "8px",
            backgroundColor: "#fff",
            border: "1px solid #e0e0e0",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            borderRadius: "8px",
          }}
        >
          <Grid item xs={6} px={1} pb={1}>
            <TextFieldLabel title={"Class Stream"} required />
            <Controller
              control={control}
              rules={{
                required: "Class required",
              }}
              render={({
                field: { onChange, onBlur, value },
                fieldState: { error },
              }) => (
                <>
                  <CustomSelectBox
                    native
                    displayEmpty
                    fullWidth
                    size="small"
                    onBlur={onBlur}
                    onChange={onChange}
                    value={value}
                    error={!!error}
                  >
                    <MenuItem value="" component="option">
                      Select
                    </MenuItem>
                    {classData?.map((item) => (
                      <MenuItem
                        component="option"
                        key={item?.classDocId}
                        value={item?.classDocId}
                      >
                        {item?.className_stream}
                      </MenuItem>
                    ))}
                  </CustomSelectBox>
                  {!!error && <ErrorText text={error?.message} />}
                </>
              )}
              name="fromClassDocId"
            />
          </Grid>
          <Grid item xs={6} px={1} pb={1}>
            <TextFieldLabel title={"Section"} required />
            <CustomSelect
              control={control}
              name="fromSection"
              rules={{ required: "Section required" }}
              options={state?.sections ?? []}
            />
          </Grid>
        </Grid>
      </>
    );
  };
  const nextSessionBox = () => {
    return (
      <>
        <Stack direction="row" alignItems="center" gap={2} sx={{ mb: 1 }}>
          <TextFieldLabel title={`Enter the details for promotion/demotion`} />
        </Stack>
        <Grid
          container
          spacing={1}
          sx={{
            padding: "2",
            marginTop: "8px",
            backgroundColor: "#fff",
            border: "1px solid #e0e0e0",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            borderRadius: "8px",
          }}
        >
          <Grid item xs={3} px={1} pt={1}>
            <TextFieldLabel title={`Next Session : `} />
            <CustomSelectBox
              native
              size="small"
              displayEmpty
              value={nextSession}
              onChange={(e) => {
                setNextSession(e.target.value);
                setNextSessionError("");
              }}
            >
              <MenuItem value="" component="option">
                <em>Select</em>
              </MenuItem>
              {allSessions.map((s) => (
                <MenuItem key={s._id} value={s.sessionName} component="option">
                  {s.sessionName}
                </MenuItem>
              ))}
            </CustomSelectBox>
            {nextSessionError && (
              <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                {nextSessionError}
              </Typography>
            )}
          </Grid>
          <Grid item xs={3} px={1} pb={1}>
            <TextFieldLabel title={"Admission Date"} required />
            <CustomDatePicker
              control={control}
              name="admissionDate"
              rules={{
                required: "Admission Date required",
              }}
            />
          </Grid>
          <Grid item xs={3} px={1} pb={1}>
            <TextFieldLabel title={"Class Stream"} required />
            <Controller
              control={control}
              rules={{
                required: "Class required",
              }}
              render={({
                field: { onChange, onBlur, value },
                fieldState: { error },
              }) => (
                <>
                  <CustomSelectBox
                    native
                    displayEmpty
                    fullWidth
                    size="small"
                    onBlur={onBlur}
                    onChange={onChange}
                    value={value}
                    error={!!error}
                  >
                    <MenuItem value="" component="option">
                      Select
                    </MenuItem>
                    {classData?.map((item) => (
                      <MenuItem
                        component="option"
                        key={item?.classDocId}
                        value={item?.classDocId}
                      >
                        {item?.className_stream}
                      </MenuItem>
                    ))}
                  </CustomSelectBox>
                  {!!error && <ErrorText text={error?.message} />}
                </>
              )}
              name="toClassDocId"
            />
          </Grid>
          <Grid item xs={3} px={1} pb={1}>
            <TextFieldLabel title={"Section"} required />
            <CustomSelect
              control={control}
              name="toSection"
              rules={{ required: "Section required" }}
              options={state?.toSections ?? []}
            />
          </Grid>
        </Grid>
      </>
    );
  };

  useEffect(() => {
    const selectedClass = watch("fromClassDocId");
    if (selectedClass) {
      const selectedSections = classData?.find(
        (item) => item.classDocId === selectedClass,
      )?.sections;
      setState((prev) => ({
        ...prev,
        sections: selectedSections,
      }));
    }
  }, [classData, watch("fromClassDocId")]);

  useEffect(() => {
    if (watch("toClassDocId")) {
      const toSections = classData?.find(
        (item) => item?.classDocId === watch("toClassDocId"),
      )?.sections;

      setState((prev) => ({
        ...prev,
        toSections: toSections,
      }));
    }
  }, [classData, watch("toClassDocId")]);
  const handleConfirmDialogOpen = (data) => {
    if (selectedPromotionStudents.length === 0) {
      toast.error("Please select at least one student to promote/demote");
      return;
    }
    setState((prev) => ({ ...prev, confirmDialogOpen: true, formData: data }));
  };

  const handleConfirmDialogClose = () => {
    setState((prev) => ({ ...prev, confirmDialogOpen: false }));
  };

  const handleConfirmPromotion = () => {
    if (!nextSession) {
      setNextSessionError("Please select the next session");
      return;
    }
    if (state.formData) {
      const payload = {
        fromClass: {
          session: selectedSession,
          classDocId: state.formData.fromClassDocId,
          section: state.formData.fromSection,
          reqStudentDocIdList: selectedPromotionStudents.map(
            (item) => item.studentDocId,
          ),
        },
        toClass: {
          session: nextSession,
          admissionDate: state.formData.admissionDate
            ? new Date(state.formData.admissionDate)
            : null,
          classDocId: state.formData.toClassDocId,
          section: state.formData.toSection,
        },
      };
      bulkPromoteByClassMutate(payload);
    }
  };

  const handleReset = () => {
    reset(initialState);
    setSelectedPromotionStudents([]);
  };

  return (
    <>
      {/* <CustomLinearProgress
        loading={getClassDropdownFetching || getClassDropdownLoading}
      /> */}

      <Dialog
        open={state.confirmDialogOpen}
        onClose={() => handleConfirmDialogClose()}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "10px",
            padding: "20px",
            backgroundColor: "#ffffff",
            boxShadow: 3,
          },
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <Grid2 container spacing={2}>
            <Grid2
              size={{ xs: 12, md: 12 }}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <Info
                sx={{
                  fontSize: "50px",
                  color: "secondary",
                  marginBottom: 2,
                  textAlign: "center",
                  alignSelf: "center",
                  justifySelf: "center",
                  display: "flex",
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: "#0C2F49",
                  textAlign: "center",
                }}
              >
                Promote
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: "#0C2F49",
                  textAlign: "center",
                  marginTop: 2,
                }}
              >
                Are you sure you want to promote{" "}
                {selectedPromotionStudents.length} student
                {selectedPromotionStudents.length === 1 ? "" : "s"}?
              </Typography>
            </Grid2>
          </Grid2>
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              handleConfirmDialogClose();
            }}
            sx={{
              width: "100%",
              marginTop: 2,
              borderRadius: "40px",
              backgroundColor: "primary",
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            loading={bulkPromoteByClassLoading}
            variant="contained"
            color="secondary"
            onClick={() => {
              handleConfirmPromotion();
            }}
            sx={{
              width: "100%",
              marginTop: 2,
              borderRadius: "40px",
            }}
          >
            Promote
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Stack
        direction="row"
        justifyContent="flex-end"
        sx={{ mt: 1 }}
        spacing={2}
      >
        <Button
          variant="outlined"
          size="medium"
          type="reset"
          form="bulkPromo"
          color="error"
        >
          Reset
        </Button>
        <LoadingButton
          variant="contained"
          size="medium"
          color="secondary"
          type="submit"
          form="bulkPromo"
          loading={bulkPromoteByClassLoading}
        >
          Proceed
        </LoadingButton>
      </Stack>

      <form
        onSubmit={handleSubmit(handleConfirmDialogOpen)}
        onReset={handleReset}
        id="bulkPromo"
      >
        <Grid
          container
          spacing={isMobile ? 0 : 2}
          sx={{ padding: isMobile ? 1 : 0 }}
        >
          <Grid item xs={isMobile ? 12 : 4}>
            {currentSessionBox()}
          </Grid>
          <Grid item xs={isMobile ? 12 : 8}>
            {nextSessionBox()}
          </Grid>
        </Grid>
      </form>

      {fromClassDocId && fromSection && (
        <>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1, mt: 1 }}
          >
            <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
              Total Student :{" "}
              <span style={{ fontSize: "16px" }}>
                {promotionStudents?.length || 0}
              </span>
            </Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
              Selected :{" "}
              <span style={{ fontSize: "16px" }}>
                {selectedPromotionStudents?.length || 0}
              </span>
            </Typography>
          </Stack>
          <CommonTable1
            columns={promotionStudentsColumns}
            data={promotionStudents}
            state={{ isLoading: promotionStudentsFetching }}
            maxHeight={isMobile ? "65vh" : "50vh"}
            multiSelect
            itemLabel="Student"
            onDataChange={handleStudentSelectionChange}
          />
        </>
      )}
    </>
  );
};

export default BulkPromotion;
